import { get } from 'lodash';

import { getNameOfLeashContentFieldById } from 'leash/utilities/localStorage/leashContentStorage';
import { PackageSearchCategories, UpdateTravelMethodParameters } from 'leash/types';
import { defaultSort } from 'utilities/sorts/sorts';
import { FacetList, TravelPlan, TableFilter } from 'types';
import { PackageSearchCategory } from 'leash/utilities/const';

export interface PackagesState {
  loading: boolean;
  packages: TravelPlan[];
  facets: FacetList;
  filters: TableFilter;
  fetchPackages: boolean;
}

export const packagesInitState: PackagesState = {
  loading: false,
  packages: [],
  facets: {},
  filters: {},
  fetchPackages: true,
};

export type PackagesAction =
  | { type: 'LOAD_PACKAGES' }
  | { type: 'SET_FETCH_PACKAGES' }
  | { type: 'SET_PACKAGES'; payload: { packages: TravelPlan[]; facets: FacetList } }
  | { type: 'FILTER_PACKAGES'; payload: TableFilter }
  | { type: 'SEARCH_PACKAGES'; query: string; category: PackageSearchCategories }
  | {
      type: 'SEARCH_TRAVEL_PROPOSAL_FIELDS';
      query: string;
      category: PackageSearchCategories;
    }
  | { type: 'SET_TRAVEL_METHOD'; payload: UpdateTravelMethodParameters };

export const searchPackages = (state: PackagesState, category: string, query: string) =>
  state.packages.filter((p: TravelPlan) => {
    if (!get(p, category)) return false;
    const packageField = String(get(p, category))?.toLowerCase().trim();
    return packageField?.includes(query.toLowerCase());
  });

export const packagesReducer = (
  state: PackagesState,
  action: PackagesAction,
): PackagesState => {
  switch (action.type) {
    case 'LOAD_PACKAGES': {
      return { ...state, loading: true };
    }
    case 'SET_FETCH_PACKAGES': {
      return { ...state, fetchPackages: true };
    }
    case 'SET_PACKAGES': {
      const { facets, packages } = action.payload;
      packages.sort((packageA, packageB) =>
        defaultSort(packageB.leadId, packageA.leadId),
      );
      return {
        ...state,
        loading: false,
        fetchPackages: false,
        facets,
        packages,
      };
    }
    case 'FILTER_PACKAGES': {
      const filters = { ...action.payload };
      return { ...state, filters };
    }
    case 'SEARCH_PACKAGES': {
      const { category, query } = action;
      const packages = searchPackages(state, category, query);

      return { ...state, packages };
    }
    case 'SEARCH_TRAVEL_PROPOSAL_FIELDS': {
      const { category, query } = action;
      const categoryTravelProposal = `travelPackage.travelProposal.${category}`;
      if (
        category === PackageSearchCategory.Airport ||
        category === PackageSearchCategory.Transporter
      ) {
        const destinationCategory =
          category === PackageSearchCategory.Airport
            ? categoryTravelProposal.replace(
                'originAirportName',
                'destinationAirportName',
              )
            : categoryTravelProposal.replace(
                'firstMileTransporterCompanyName',
                'lastMileTransporterCompanyName',
              );

        const packages = state.packages.filter((p: TravelPlan) => {
          if (!get(p, categoryTravelProposal)) return false;
          const packageField = String(get(p, categoryTravelProposal))
            ?.toLowerCase()
            .trim();
          const destinationField = String(get(p, destinationCategory))
            ?.toLowerCase()
            .trim();
          return (
            packageField?.includes(query.toLowerCase()) ||
            destinationField?.includes(query.toLowerCase())
          );
        });

        return { ...state, packages };
      }

      const packages = searchPackages(state, categoryTravelProposal, query);

      return { ...state, packages };
    }
    case 'SET_TRAVEL_METHOD': {
      const clonedPackages = [...state.packages];
      const { orderId, travelMethodId } = action.payload;
      const updatedPackage = clonedPackages.find((p) => p.orderId === orderId);
      if (updatedPackage) {
        updatedPackage.travelPackage.travelMethodId = travelMethodId;
        updatedPackage.travelPackage.travelMethod = getNameOfLeashContentFieldById(
          'travelMethods',
          travelMethodId,
        );
      }
      return { ...state, packages: clonedPackages };
    }
    default:
      return state;
  }
};
